import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom, tap
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { GatewayStoreAction } from "src/components/shared-components/enum/enumStoreAction";
import { GenericDeviceCardListStore } from "src/model/interface/generic-device-card-list-store";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { GatewayService } from "src/service/gateway.service";
import {
  gatewayLoadErrorAction,
  gatewayLoadErrorHandlingAction,
  gatewayLoadSuccessAction,
  gatewayLoadWithOldDataAction,
  gatewaySettingsLoadErrorAction,
  gatewaySettingsLoadErrorHandlingAction,
  gatewaySettingsLoadSuccessAction,
  gatewaySettingsLoadWithOldDataAction
} from "../actions/gateway.actions";
import { currentUserData } from "../selectors/authentication.selectors";
import { listGateway, listGatewaySettings } from "../selectors/gateway.selectors";

/**
 * Handle gateway effects
 */
@Injectable()
export class GatewayEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store,
    private _gatewayService: GatewayService
  ) { }

  /**
   * Load full list of Gateways
   */
  public GatewayListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(GatewayStoreAction.GATEWAY_LOAD_START),
      withLatestFrom(this._store.select(currentUserData)),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      switchMap((action: any) => {
        return this._gatewayService.getGateway().pipe(
          map((gatewayListApiData: GenericApiData) =>
            gatewayLoadSuccessAction({
              gatwayApiData: gatewayListApiData,
            })
          ),
          catchError((error: HttpErrorResponse) => {
            return of(gatewayLoadErrorHandlingAction(error));
          })
        );
      })
    )
  );

  /**
   * Effect handling error in gateway list loading
   */
  public GatewayListLoadingErrorHandlingEffects = createEffect(() =>
    this._actions$.pipe(
      ofType(GatewayStoreAction.GATEWAY_LOAD_ERROR_HANDLING),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listGateway)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((action: any) => {
        const httpError: HttpErrorResponse = action[0] as HttpErrorResponse;
        const gatewayListStore: GenericStoreApiData = action[2];

        //When API return erro, but previos snapshot store
        //was populate correctly, it returns previous card list
        //otherwiser return error

        //if (deviceCardStore.loaded && !deviceCardStore.error) {
        if (
          gatewayListStore?.loaded &&
          !gatewayListStore.errorGeneric &&
          !gatewayListStore.errorNotYetImplemented
        ) {
          return gatewayLoadWithOldDataAction();
        } else {
          return gatewayLoadErrorAction();
        }
      })
    )
  );

  /**
  * Load full list of Gateways settings
  */
  public GatewaySettingsListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(GatewayStoreAction.GATEWAY_SETTINGS_LOAD_START),
      withLatestFrom(this._store.select(currentUserData)),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      switchMap((action: any) => {
        return this._gatewayService.getGatewaySettings()
          .pipe(
            map((gatewayListApiData: GenericApiData) =>
              gatewaySettingsLoadSuccessAction({
                gatwayApiData: gatewayListApiData,
              })
            ),
            catchError((error: HttpErrorResponse) => {
              return of(gatewaySettingsLoadErrorHandlingAction(error));
            })
          );
      })
    )
  );

  /**
   * Effect handling error in gateway settings list loading
   */
  public GatewaySettingsListLoadingErrorHandlingEffects = createEffect(() =>
    this._actions$.pipe(
      ofType(GatewayStoreAction.GATEWAY_SETTINGS_LOAD_ERROR_HANDLING),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listGatewaySettings)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((action: any) => {
        const httpError: HttpErrorResponse = action[0] as HttpErrorResponse;
        const gatewaySettingsListStore: GenericStoreApiData = action[2];

        //When API return erro, but previos snapshot store
        //was populate correctly, it returns previous card list
        //otherwiser return error

        //if (deviceCardStore.loaded && !deviceCardStore.error) {
        if (
          gatewaySettingsListStore?.loaded &&
          !gatewaySettingsListStore.errorGeneric &&
          !gatewaySettingsListStore.errorNotYetImplemented
        ) {
          return gatewaySettingsLoadWithOldDataAction();
        } else {
          return gatewaySettingsLoadErrorAction();
        }
      })
    )
  );
}
